import {getCurrentISOOffset, parseISODate, parseISOZonedDateTime, Temporal} from "@/shared/utils/datefunctions";
import type {DateRangeValue} from "@/shared/ui/dateRange/dateRangeState";
import type {DateTimeRangeValue} from "@/shared/ui/dateTimeRange/dateTimeRangeState";

/**
 * The unified value the {@link WorkingTimeRangePanel} exchanges with its parent: a single object carrying
 * the superset of both sub-panels' data, so the parent never juggles two mirrored ranges.
 *
 * The `ZonedDateTime` pair is the source of truth for the "when" — the day sub-panel's `PlainDate`s are
 * projected from it (dropping time/offset) and merged back onto it. `startHalfDay`/`endHalfDay` are only
 * meaningful in day/half-day mode, `breakTime` only in minute mode; the inactive mode's fields simply ride
 * along untouched.
 */
export interface WorkingTimeRangeValue {
	start: Temporal.ZonedDateTime | null;
	end: Temporal.ZonedDateTime | null;
	startHalfDay: boolean;
	endHalfDay: boolean;
	breakTime: number;
}

/**
 * Seeds a unified-value endpoint from a working-time request's wire string. The string is a full offset
 * date-time in minute mode but a date-only "YYYY-MM-DD" in day/half-day mode; a date-only endpoint is
 * anchored to midnight at the current offset so the endpoint is always a non-empty `ZonedDateTime`.
 * Returns null only for an empty/invalid string.
 */
export function parseRequestEndpoint(value: string | null | undefined): Temporal.ZonedDateTime | null {
	return parseISOZonedDateTime(value) ?? parseISODate(value)?.toZonedDateTime(getCurrentISOOffset()) ?? null;
}

/** Applies a day-mode date onto a minute-mode date-time, keeping its time/offset (midnight in the current zone when there is none yet). */
export function mergeDate(dateTime: Temporal.ZonedDateTime | null, date: Temporal.PlainDate | null): Temporal.ZonedDateTime | null {
	if (!date) {
		return null;
	}
	if (!dateTime) {
		return date.toZonedDateTime(getCurrentISOOffset());
	}
	return dateTime.with({year: date.year, month: date.month, day: date.day});
}

/** Projects the unified value onto the day sub-panel's value (drops time/offset and break time). */
export function toDateRangeValue(value: WorkingTimeRangeValue): DateRangeValue {
	return {
		start: value.start?.toPlainDate() ?? null,
		end: value.end?.toPlainDate() ?? null,
		startHalfDay: value.startHalfDay,
		endHalfDay: value.endHalfDay
	};
}

/** Projects the unified value onto the minute sub-panel's value (drops the half-day flags). */
export function toDateTimeRangeValue(value: WorkingTimeRangeValue): DateTimeRangeValue {
	return {
		start: value.start,
		end: value.end,
		breakTime: value.breakTime
	};
}

/** Folds a day sub-panel edit back into the unified value, keeping each endpoint's time/offset. */
export function foldDateRange(prev: WorkingTimeRangeValue, value: DateRangeValue): WorkingTimeRangeValue {
	return {
		...prev,
		start: mergeDate(prev.start, value.start),
		end: mergeDate(prev.end, value.end),
		startHalfDay: value.startHalfDay,
		endHalfDay: value.endHalfDay
	};
}

/** Folds a minute sub-panel edit back into the unified value (start/end carry the full date-time). */
export function foldDateTime(prev: WorkingTimeRangeValue, value: DateTimeRangeValue): WorkingTimeRangeValue {
	return {
		...prev,
		start: value.start,
		end: value.end,
		breakTime: value.breakTime
	};
}
